import React, { useState, useEffect } from 'react';
import { Modal, Progress, message } from 'antd';
import { HierarchiesList } from '../Hierarchies/HierarchiesList';
import SearchInHierarchy from './SearchInHierarchy';
import HierarchiesService from '../../../services/HierarchiesService';
import { API_URL } from '../../../constants';
import { UpdateJobProgress } from '../../../Utilities';
import Hierarchy from '../../ImageViewerHandlers/Hierarchy/Hierarchy';

interface CopyInHierarchyModalProps {
    isOpen: boolean;
    onClose: () => void;
    hierarchy: Hierarchy | null;
    sourceNodeId: string | null;
    onImportComplete: () => void;
}

interface ImportProgress {
    status: string;
    percent: number;
}

interface HierarchyRecord {
    text: string;
    label: string;
}

const CopyInHierarchyModal: React.FC<CopyInHierarchyModalProps> = ({
    isOpen,
    onClose,
    hierarchy,
    sourceNodeId,
    onImportComplete
}) => {
    const [selectedHierarchy, setSelectedHierarchy] = useState<Hierarchy | null>(null);
    const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedHierarchy(null);
            setImportProgress(null);
        }
    }, [isOpen]);

    const trackImportProgress = (taskId: string) => {
        if (!selectedHierarchy) return;

        setImportProgress({ status: "", percent: 0 });

        HierarchiesService.getImportProgress(selectedHierarchy.id, taskId)
            .then((resp) => {
                const updateProgress = new UpdateJobProgress().withDefaultUpdateProgress((progress: ImportProgress) => {
                    setImportProgress(progress);
                });

                return updateProgress.do(
                    `${API_URL}/hierarchies/${selectedHierarchy.id}/import_sub_hierarchy/${taskId}/progress`
                )
            })
            .catch((error) => {
                console.error(error);
                message.error("Failed to import sub-hierarchy.");
            })
            .finally(() => {
                setImportProgress(null);
                onImportComplete();
                onClose();
            });
    };

    if (!hierarchy) {
        return null;
    }

    return (
        <Modal
            width={700}
            title={selectedHierarchy != null ? `Import sub-hierarchy into "${selectedHierarchy.name}"` : "Select hierarchy to import"}
            visible={isOpen}
            destroyOnClose={true}
            onCancel={onClose}
            footer={null}
            bodyStyle={{ height: 'auto' }}
        >
            {importProgress !== null ? (
                <div>
                    <p>Importing sub-hierarchy...</p>
                    <Progress percent={importProgress.percent} />
                </div>
            ) : !selectedHierarchy ? (
                <HierarchiesList
                    onView={(hierarchyId: string) => {
                        HierarchiesService.fetchHierarchyById(hierarchyId).then((res) => {
                            setSelectedHierarchy(res.data.hierarchy);
                        }).catch(() => {
                            message.error("Failed to load hierarchy");
                        });
                    }}
                />
            ) : (
                <SearchInHierarchy
                    hierarchy={selectedHierarchy}
                    renderActions={(id: string, record: HierarchyRecord) => {
                        return (
                            <a
                                onClick={() => {
                                    if (!sourceNodeId) return;
                                    
                                    const sourceHierarchyId = selectedHierarchy.id;
                                    const targetNodeId = id;

                                    HierarchiesService.importSubHierarchy(
                                        hierarchy.id,
                                        sourceHierarchyId,
                                        sourceNodeId,
                                        targetNodeId
                                    )
                                        .then((taskId) => {
                                            message.success("Import started.");
                                            console.log("Import task ID:", taskId);
                                            trackImportProgress(taskId.data);
                                        })
                                        .catch((error) => {
                                            console.error("Failed to start import:", error);
                                            message.error("Failed to start import.");
                                        });
                                }}
                            >
                                Import here
                            </a>
                        );
                    }}
                />
            )}
        </Modal>
    );
};

export default CopyInHierarchyModal;
